import { useRef, useState } from "react";
import { uploadFile, uploadFilePublic, fileUrl, apiError } from "@/lib/api";
import CameraCapture from "@/components/CameraCapture";
import { Upload, Camera, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ImageField({ label, value, onChange, testid, allowCamera = true, publicMode = false }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [camOpen, setCamOpen] = useState(false);
  const [localPreview, setLocalPreview] = useState(null);

  const handleFile = async (file) => {
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setLocalPreview(preview);
    setUploading(true);
    try {
      const path = await (publicMode ? uploadFilePublic : uploadFile)(file);
      onChange(path);
    } catch (e) {
      setLocalPreview(null);
      toast.error(apiError(e));
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setLocalPreview(null);
    onChange(null);
  };

  const previewSrc = localPreview || (value ? fileUrl(value) : null);

  return (
    <div>
      {label && (
        <label className="block text-xs font-semibold uppercase tracking-[0.1em] text-zinc-400 mb-2">
          {label}
        </label>
      )}
      <div className="relative border border-zinc-800 rounded-sm bg-[#0a0a0a] aspect-video overflow-hidden flex items-center justify-center">
        {previewSrc ? (
          <>
            <img src={previewSrc} alt={label} className="w-full h-full object-cover" />
            {uploading && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-primary animate-spin" />
              </div>
            )}
            {!uploading && (
              <button
                type="button"
                data-testid={`${testid}-remove`}
                onClick={removeImage}
                className="absolute top-2 right-2 bg-black/70 hover:bg-primary text-white p-1.5 rounded-sm transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </>
        ) : uploading ? (
          <Loader2 className="w-6 h-6 text-primary animate-spin" />
        ) : (
          <div className="flex flex-col items-center gap-3 p-4">
            <div className="flex gap-2">
              <button
                type="button"
                data-testid={`${testid}-upload`}
                onClick={() => inputRef.current?.click()}
                className="flex items-center gap-2 border border-zinc-700 text-zinc-300 hover:text-white hover:border-primary rounded-sm px-4 py-2 text-sm transition-colors"
              >
                <Upload className="w-4 h-4" /> Tải ảnh
              </button>
              {allowCamera && (
                <button
                  type="button"
                  data-testid={`${testid}-camera`}
                  onClick={() => setCamOpen(true)}
                  className="flex items-center gap-2 bg-primary/90 hover:bg-primary text-white rounded-sm px-4 py-2 text-sm transition-colors"
                >
                  <Camera className="w-4 h-4" /> Chụp
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      <CameraCapture
        open={camOpen}
        onClose={() => setCamOpen(false)}
        onCapture={handleFile}
        title={`Chụp ${label || "ảnh"}`}
      />
    </div>
  );
}
